import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from 'auth/Auth';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DataTable from 'examples/Tables/DataTable';

const formatCategory = (value) => {
    if (!value) return '—';
    return String(value).replace(/_/g, ' ');
};

/**
 * Search package components from other trips and import into the current trip.
 * When attachContext is set, imports and selects the component on an itinerary line item.
 */
const SearchExistingComponentsModal = ({
    open,
    onClose,
    tripId,
    onImport,
    attachContext = null,
    isImporting = false,
}) => {
    const { axiosInstance } = useAuth();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const fetchComponents = useCallback(async () => {
        if (!open) return;
        setIsLoading(true);
        setError('');
        try {
            const params = { limit: 100 };
            if (debouncedQuery) params.q = debouncedQuery;
            if (tripId) params.exclude_trip_id = tripId;

            const response = await axiosInstance.get('/trip-wizard/lists/package-components', { params });
            setRows(response.data || []);
        } catch (err) {
            console.error('Error searching trip components:', err);
            setError('Unable to search existing trip logistics. Please try again.');
            setRows([]);
        } finally {
            setIsLoading(false);
        }
    }, [axiosInstance, debouncedQuery, open, tripId]);

    useEffect(() => {
        if (open) {
            setQuery('');
            setDebouncedQuery('');
            setSelectedId(null);
            setError('');
        }
    }, [open]);

    useEffect(() => {
        fetchComponents();
    }, [fetchComponents]);

    const tableData = useMemo(() => {
        const columns = [
            { Header: 'Trip Logistics', accessor: 'name', width: '22%' },
            { Header: 'Category', accessor: 'category', width: '14%' },
            { Header: 'Trip', accessor: 'trip_title', width: '22%' },
            { Header: 'Package', accessor: 'package_title', width: '22%' },
            {
                Header: 'Cost',
                accessor: 'cost_price',
                width: '12%',
                Cell: ({ row }) => {
                    const { cost_price: price, currency } = row.original;
                    if (price == null || price === '') return '—';
                    return `${(currency || '').toUpperCase()} ${price}`;
                },
            },
            {
                Header: '',
                accessor: 'actions',
                width: '8%',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <MDButton
                        size="small"
                        variant={selectedId === row.original.id ? 'gradient' : 'outlined'}
                        color="info"
                        onClick={() => setSelectedId(row.original.id)}
                    >
                        {selectedId === row.original.id ? 'Selected' : 'Select'}
                    </MDButton>
                ),
            },
        ];

        const tableRows = rows.map((row) => ({
            ...row,
            name: row.name || '—',
            category: formatCategory(row.category),
            trip_title: row.trip_title || '—',
            package_title: row.package_title || '—',
        }));

        return { columns, rows: tableRows };
    }, [rows, selectedId]);

    const selectedRow = rows.find((row) => row.id === selectedId);

    const handleImport = async () => {
        if (!selectedId || !onImport) return;
        await onImport(selectedId, selectedRow, attachContext);
    };

    const importLabel = attachContext ? 'Import & Attach to Day' : 'Import to This Trip';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Search Existing Trip Logistics</DialogTitle>
            <DialogContent dividers>
                <MDTypography variant="body2" color="text" mb={2}>
                    {attachContext
                        ? `Find a service from another trip package and attach it to Day ${attachContext.dayNumber || attachContext.dayIndex + 1}.`
                        : 'Find a service from another trip package and add it to this trip.'}
                </MDTypography>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by trip logistics, category, trip, or package…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isLoading ? (
                    <MDBox py={6} display="flex" justifyContent="center">
                        <CircularProgress size={28} />
                    </MDBox>
                ) : rows.length === 0 ? (
                    <MDBox py={4} textAlign="center">
                        <MDTypography variant="body2" color="text">
                            No matching trip logistics found.
                        </MDTypography>
                    </MDBox>
                ) : (
                    <Box sx={{ '& th:last-child, & td:last-child': { textAlign: 'right' } }}>
                        <DataTable
                            table={tableData}
                            canSearch={false}
                            entriesPerPage={{ defaultValue: 8, entries: [8, 15, 25] }}
                            showTotalEntries={false}
                            isSorted={false}
                            noEndBorder
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton variant="outlined" color="dark" onClick={onClose} disabled={isImporting}>
                    Cancel
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleImport}
                    disabled={!selectedId || isImporting}
                >
                    {isImporting ? 'Importing…' : importLabel}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default SearchExistingComponentsModal;
